import React from 'react';

import { datadogRum } from '@datadog/browser-rum';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    datadogRum.addError(error, { componentStack: errorInfo.componentStack });

    if (process.env.NODE_ENV !== 'production') {
      console.log(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex h-full w-full items-center justify-center p-8">
            <div className="max-w-md text-center">
              <div className="flex flex-col items-center gap-8">
                <div>
                  <h2 className="mb-10 font-serif text-3xl font-medium text-gray-900">Something went wrong...</h2>
                  <div className="text-sm text-balance text-gray-600">
                    An unexpected error occurred. Please try refreshing the page. If this issue persists,{' '}
                    <a className="text-blue-500 hover:underline" href="mailto:support@wexler.ai">
                      contact support@wexler.ai
                    </a>
                    .
                  </div>
                </div>

                <Button variant="ghost" className="group" onClick={() => window.location.reload()}>
                  <Icon className="lucide--refresh-ccw duration-400 ease-in-out group-hover:rotate-270" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
